import api from './index';
import { buildAdminApiRequest } from './admin';

export type AdminPodActionStatus =
  | 'requested'
  | 'success'
  | 'failure'
  | 'unsupported';

export type AdminPod = {
  name: string;
  source?: 'tv-testing-ui' | 'creative-testing';
  controlPlane?: 'tv-testing-ui' | 'creative-testing';
  isOnline: boolean;
  isReady: boolean;
  connectionStatus: string;
  debugConnectionStatus: string;
  isTvOnline: boolean;
  isVideoOnline: boolean;
  isPodAvailable?: boolean;
  activeClients: unknown[];
  lastActive: string;
  lastActiveAt: number | null;
  lastUpdatedAt: number | null;
  remoteAddress?: string;
  metadata: Record<string, unknown>;
  capabilities: string[];
  supportedActions: {
    wakeup: boolean;
    videoReload: boolean;
    restartPuppeteer: boolean;
    restartPodProcess: boolean;
    readConfiguration: boolean;
    updateConfiguration: boolean;
    pauseSelection: boolean;
    smartifiSwitch: boolean;
  };
  smartifi: {
    supported: boolean;
    switchId: string;
    state: string;
  };
};

export type AdminPodActionResponse<TData = unknown> = {
  success: boolean;
  pod: string;
  action: string;
  data?: TData;
  error?: string;
};

export type AdminPodInventoryPartialError = {
  source: string;
  message: string;
  detail?: string;
  statusCode?: number;
};

export type AdminPodInventoryResponse = {
  pods: AdminPod[];
  partialErrors: AdminPodInventoryPartialError[];
};

export type AdminPodActionLog = {
  id: string;
  pod: string;
  action: string;
  status: AdminPodActionStatus;
  actor: {
    email?: string;
    name?: string;
    id?: string;
  };
  request: Record<string, unknown>;
  response: Record<string, unknown>;
  error: string;
  createdAt: string | null;
};

export type AdminPodActionLogFilters = {
  pod?: string;
  action?: string;
  status?: AdminPodActionStatus;
  limit?: number;
};

export type AdminPodActionAuditCreate = {
  pod: string;
  action: string;
  request?: Record<string, unknown>;
};

export type AdminPodActionAuditUpdate = {
  id: string;
  pod: string;
  action: string;
  status: Extract<AdminPodActionStatus, 'success' | 'failure' | 'unsupported'>;
  response?: Record<string, unknown>;
  error?: string;
};

export type AdminPodConfigurationUpdate = {
  pod: string;
  configuration: Record<string, unknown>;
  restartAfterUpdate?: boolean;
};

const normalizeAdminPodInventoryResponse = (
  response: AdminPod[] | Partial<AdminPodInventoryResponse>,
): AdminPodInventoryResponse => {
  if (Array.isArray(response)) {
    return {
      pods: response,
      partialErrors: [],
    };
  }

  return {
    pods: Array.isArray(response?.pods) ? response.pods : [],
    partialErrors: Array.isArray(response?.partialErrors)
      ? response.partialErrors
      : [],
  };
};

const adminPodManagementApi = api.injectEndpoints({
  endpoints: build => ({
    getAdminPods: build.query<AdminPodInventoryResponse, void>({
      query: () =>
        buildAdminApiRequest({
          method: 'GET',
          path: '/pods',
        }),
      transformResponse: normalizeAdminPodInventoryResponse,
      providesTags: [{ type: 'adminPods', id: 'LIST' }],
    }),
    wakeupAdminPod: build.mutation<
      AdminPodActionResponse,
      { pod: string; command?: string }
    >({
      query: ({ pod, command }) =>
        buildAdminApiRequest({
          method: 'POST',
          path: `/pods/${encodeURIComponent(pod)}/actions/wakeup`,
          body: command ? { command } : {},
        }),
      invalidatesTags: [
        { type: 'adminPods', id: 'LIST' },
        { type: 'adminPodActionLogs', id: 'LIST' },
      ],
    }),
    reloadAdminPodVideo: build.mutation<AdminPodActionResponse, string>({
      query: pod =>
        buildAdminApiRequest({
          method: 'POST',
          path: `/pods/${encodeURIComponent(pod)}/actions/video-reload`,
        }),
      invalidatesTags: [
        { type: 'adminPods', id: 'LIST' },
        { type: 'adminPodActionLogs', id: 'LIST' },
      ],
    }),
    restartAdminPodPuppeteer: build.mutation<
      AdminPodActionResponse,
      { pod: string; processId?: string }
    >({
      query: ({ pod, processId }) =>
        buildAdminApiRequest({
          method: 'POST',
          path: `/pods/${encodeURIComponent(pod)}/actions/restart-puppeteer`,
          body: { processId: processId || '0' },
        }),
      invalidatesTags: [
        { type: 'adminPods', id: 'LIST' },
        { type: 'adminPodActionLogs', id: 'LIST' },
      ],
    }),
    restartAdminPodProcess: build.mutation<
      AdminPodActionResponse,
      { pod: string; delayMs?: number }
    >({
      query: ({ pod, delayMs }) =>
        buildAdminApiRequest({
          method: 'POST',
          path: `/pods/${encodeURIComponent(pod)}/actions/restart-pod-process`,
          body: delayMs ? { delayMs } : {},
        }),
      invalidatesTags: [
        { type: 'adminPods', id: 'LIST' },
        { type: 'adminPodActionLogs', id: 'LIST' },
      ],
    }),
    updateAdminPodSelectionStatus: build.mutation<
      AdminPodActionResponse,
      { pod: string; enabled: boolean }
    >({
      query: ({ pod, enabled }) =>
        buildAdminApiRequest({
          method: 'POST',
          path: `/pods/${encodeURIComponent(pod)}/actions/status`,
          body: { enabled },
        }),
      invalidatesTags: [
        { type: 'adminPods', id: 'LIST' },
        { type: 'adminPodActionLogs', id: 'LIST' },
      ],
    }),
    readAdminPodConfiguration: build.query<AdminPodActionResponse, string>({
      query: pod =>
        buildAdminApiRequest({
          method: 'GET',
          path: `/pods/${encodeURIComponent(pod)}/configuration`,
        }),
      providesTags: (_result, _error, pod) => [{ type: 'adminPods', id: pod }],
    }),
    updateAdminPodConfiguration: build.mutation<
      AdminPodActionResponse,
      AdminPodConfigurationUpdate
    >({
      query: ({ pod, configuration, restartAfterUpdate }) =>
        buildAdminApiRequest({
          method: 'PUT',
          path: `/pods/${encodeURIComponent(pod)}/configuration`,
          body: {
            configuration,
            restartAfterUpdate,
          },
        }),
      invalidatesTags: (_result, _error, arg) => [
        { type: 'adminPods', id: 'LIST' },
        { type: 'adminPods', id: arg.pod },
        { type: 'adminPodActionLogs', id: 'LIST' },
      ],
    }),
    updateAdminPodSmartifiSwitch: build.mutation<
      AdminPodActionResponse,
      { pod: string; powerState: 'on' | 'off'; switchId?: string }
    >({
      query: ({ pod, powerState, switchId }) =>
        buildAdminApiRequest({
          method: 'POST',
          path: `/pods/${encodeURIComponent(pod)}/actions/smartifi-switch`,
          body: {
            powerState,
            ...(switchId ? { switchId } : {}),
          },
        }),
      invalidatesTags: [
        { type: 'adminPods', id: 'LIST' },
        { type: 'adminPodActionLogs', id: 'LIST' },
      ],
    }),
    getAdminPodActionLogs: build.query<
      AdminPodActionLog[],
      AdminPodActionLogFilters | void
    >({
      query: filters => {
        const activeFilters = filters || {};

        return buildAdminApiRequest({
          method: 'GET',
          path: '/pod-action-logs',
          params: {
            ...(activeFilters.pod ? { pod: activeFilters.pod } : {}),
            ...(activeFilters.action ? { action: activeFilters.action } : {}),
            ...(activeFilters.status ? { status: activeFilters.status } : {}),
            ...(activeFilters.limit ? { limit: activeFilters.limit } : {}),
          },
        });
      },
      providesTags: result =>
        result
          ? [
              { type: 'adminPodActionLogs', id: 'LIST' },
              ...result.map(item => ({
                type: 'adminPodActionLogs' as const,
                id: item.id,
              })),
            ]
          : [{ type: 'adminPodActionLogs', id: 'LIST' }],
    }),
    createAdminPodActionAudit: build.mutation<
      { id: string; pod: string; action: string; status: AdminPodActionStatus },
      AdminPodActionAuditCreate
    >({
      query: body =>
        buildAdminApiRequest({
          method: 'POST',
          path: '/pod-action-logs',
          body,
        }),
      invalidatesTags: [{ type: 'adminPodActionLogs', id: 'LIST' }],
    }),
    updateAdminPodActionAudit: build.mutation<
      { id: string; pod: string; action: string; status: AdminPodActionStatus },
      AdminPodActionAuditUpdate
    >({
      query: ({ id, ...body }) =>
        buildAdminApiRequest({
          method: 'PATCH',
          path: `/pod-action-logs/${encodeURIComponent(id)}`,
          body,
        }),
      invalidatesTags: [{ type: 'adminPodActionLogs', id: 'LIST' }],
    }),
  }),
});

export const {
  useCreateAdminPodActionAuditMutation,
  useGetAdminPodsQuery,
  useGetAdminPodActionLogsQuery,
  useLazyReadAdminPodConfigurationQuery,
  useReadAdminPodConfigurationQuery,
  useReloadAdminPodVideoMutation,
  useRestartAdminPodProcessMutation,
  useRestartAdminPodPuppeteerMutation,
  useUpdateAdminPodConfigurationMutation,
  useUpdateAdminPodActionAuditMutation,
  useUpdateAdminPodSelectionStatusMutation,
  useUpdateAdminPodSmartifiSwitchMutation,
  useWakeupAdminPodMutation,
} = adminPodManagementApi;
